import { Point, Rect } from '../math/Shapes.js';
import { forEach, isUndefined } from '../utils/functions.js';
import { deepExtend } from '../utils/deep-extend.js';

const LayoutDefaultOptions = {
    type: 'Tree',
    subtype: 'Down',
    roots: null,
    animate: false,
    // -------------------------------------------------------------------
    /**
     * Force-directed option: whether the motion of the nodes should be limited by the boundaries of the diagram surface.
     */
    limitToView: false,
    /**
     * Force-directed option: the amount of friction applied to the motion of the nodes.
     */
    friction: 0.9,
    /**
     * Force-directed option: the optimal distance between nodes (minimum energy).
     */
    nodeDistance: 50,
    /**
     * Force-directed option: the number of time things are being calculated.
     */
    iterations: 300,
    // -------------------------------------------------------------------
    /**
     * Tree option: the separation in one direction (depends on the subtype what direction this is).
     */
    horizontalSeparation: 90,
    /**
     * Tree option: the separation in the complementary direction (depends on the subtype what direction this is).
     */
    verticalSeparation: 50,
    // -------------------------------------------------------------------
    /**
     * Tip-over tree option: children-to-parent vertical distance.
     */
    underneathVerticalTopOffset: 15,
    /**
     * Tip-over tree option: children-to-parent horizontal distance.
     */
    underneathHorizontalOffset: 15,
    /**
     * Tip-over tree option: leaf-to-next-branch vertical distance.
     */
    underneathVerticalSeparation: 15,
    // -------------------------------------------------------------------
    /**
     * Settings object to organize the different components of the diagram in a grid layout structure
     */
    grid: {
        /**
         * The width of the grid in which components are arranged. Beyond this width a component will be on the next row.
         */
        width: 1500,
        /**
         * The left offset of the grid.
         */
        offsetX: 50,
        /**
         * The top offset of the grid.
         */
        offsetY: 50,
        /**
         * The horizontal padding within a cell of the grid where a single component resides.
         */
        componentSpacingX: 20,
        /**
         * The vertical padding within a cell of the grid where a single component resides.
         */
        componentSpacingY: 20
    },
    // -------------------------------------------------------------------
    /**
     * Layered option: the separation height/width between the layers.
     */
    layerSeparation: 50,
    /**
     * Layered option: how many rounds of shifting and fine-tuning.
     */
    layeredIterations: 2,
    /**
     * Tree-radial option: the angle at which the layout starts.
     */
    startRadialAngle: 0,
    /**
     * Tree-radial option: the angle at which the layout starts.
     */
    endRadialAngle: 360,
    /**
     * Tree-radial option: the separation between levels.
     */
    radialSeparation: 150,
    /**
     * Tree-radial option: the separation between the root and the first level.
     */
    radialFirstLevelSeparation: 200,
    /**
     * Tree-radial option: whether a virtual roots bing the components in one radial layout.
     */
    keepComponentsInOneRadialLayout: false,
    // -------------------------------------------------------------------
    // TODO: ensure to change this to false when containers are around
    ignoreContainers: true,
    layoutContainerChildren: false,
    ignoreInvisible: true,
    animateTransitions: false
};
/**
 * Base class for layout algorithms.
 *
 * @type {*}
 */
class LayoutBase {
    constructor() {
        this.defaultOptions = { ...LayoutDefaultOptions, grid: { ...LayoutDefaultOptions.grid } };
    }
    /**
     * Organizes the components in a grid.
     * Returns the final set of nodes (not the Graph).
     *
     * @param components
     */
    gridLayoutComponents(components) {
        if (!components) {
            throw new Error('No components supplied.');
        }
        // calculate and cache the bounds of the components
        forEach(components, function (c) {
            c.calcBounds();
        });
        // order by decreasing width
        components.sort(function (a, b) {
            return b.bounds.width - a.bounds.width;
        });
        const maxWidth = this.options.grid.width, offsetX = this.options.grid.componentSpacingX, offsetY = this.options.grid.componentSpacingY, startX = this.options.grid.offsetX, startY = this.options.grid.offsetY, resultLinkSet = [], resultNodeSet = [];
        let height = 0, x = startX, y = startY;
        while (components.length > 0) {
            if (x >= maxWidth) {
                // start a new row
                x = startX;
                y += height + offsetY;
                // reset the row height
                height = 0;
            }
            const component = components.pop();
            this.moveToOffset(component, new Point(x, y));
            for (let i = 0; i < component.nodes.length; i++) {
                resultNodeSet.push(component.nodes[i]); // to be returned in the end
            }
            for (let i = 0; i < component.links.length; i++) {
                resultLinkSet.push(component.links[i]);
            }
            const boundingRect = component.bounds;
            let currentHeight = boundingRect.height;
            if (currentHeight <= 0 || isNaN(currentHeight)) {
                currentHeight = 0;
            }
            let currentWidth = boundingRect.width;
            if (currentWidth <= 0 || isNaN(currentWidth)) {
                currentWidth = 0;
            }
            if (currentHeight >= height) {
                height = currentHeight;
            }
            x += currentWidth + offsetX;
        }
        return {
            nodes: resultNodeSet,
            links: resultLinkSet
        };
    }
    moveToOffset(component, p) {
        let i, j;
        const bounds = component.bounds, deltaX = p.x - bounds.x, deltaY = p.y - bounds.y;
        for (i = 0; i < component.nodes.length; i++) {
            const node = component.nodes[i];
            let nodeBounds = node.bounds();
            if (nodeBounds.width === 0 && nodeBounds.height === 0 && nodeBounds.x === 0 && nodeBounds.y === 0) {
                nodeBounds = new Rect(0, 0, 0, 0);
            }
            nodeBounds.x += deltaX;
            nodeBounds.y += deltaY;
            node.bounds(nodeBounds);
        }
        for (i = 0; i < component.links.length; i++) {
            const link = component.links[i];
            if (link.points) {
                const newPoints = [];
                const points = link.points;
                for (j = 0; j < points.length; j++) {
                    const pt = points[j];
                    pt.x += deltaX;
                    pt.y += deltaY;
                    newPoints.push(pt);
                }
                link.points = newPoints;
            }
        }
        this.currentHorizontalOffset += bounds.width + this.options.grid.offsetX;
        return new Point(deltaX, deltaY);
    }
    transferOptions(options) {
        // Size options lead to stackoverflow and need special handling
        this.options = deepExtend({}, this.defaultOptions);
        if (isUndefined(options)) {
            return;
        }
        this.options = deepExtend(this.options, options || {});
    }
}

export { LayoutBase, LayoutDefaultOptions };
